import { DerbyRngV1, hashFnv1a } from '../rng';
import { LEAGUE_DEFS } from '../race/params';
import type { HorseStats, StatId } from '../race/types';
import { inheritGenotype, randomGenotype } from './genetics';
import { ageFactor, horseFromGenotype } from './horse';
import type { Horse, HorseId } from './types';

/**
 * The rivals: six named horses per ladder league who age, win offscreen,
 * retire into the stud pool, and get replaced — sometimes by their own
 * sons. They ARE the fields you race against, so losses have faces.
 */

export type RivalStyle = 'pacer' | 'sprinter' | 'closer';

export interface RivalInfo {
  horseId: HorseId;
  league: number;
  style: RivalStyle;
  /** Execution quality 0.75..0.97 — how much of the horse they get out. */
  polish: number;
  /** Hidden effective-stat floor, recalculated only at season turnover. */
  statFloor: HorseStats;
}

export const RIVALS_PER_LEAGUE = 6;

const RIVAL_POOL = [
  'Copper Bell',
  'Midnight Ledger',
  'Prairie Comet',
  'Old Tobacco',
  'Silver Furlong',
  'Dust Devil',
  'Honest Abacus',
  'Clover Gambit',
  'Iron Meadow',
  'Whistling Jenny',
  'Barnstormer',
  'Quiet Thunder',
  'Penny Dreadful',
  'Cast Iron Alibi',
  'Velvet Hammer',
  'Sunday Edition',
  'Loose Cannonade',
  'Marble Rye',
  'Fifth Furlong',
  'Borrowed Lightning',
  'Tin Rooster',
  'Grand Larceny',
  'Mild Peril',
  'Last Lantern',
] as const;

const STYLES: RivalStyle[] = ['pacer', 'sprinter', 'closer'];

export function rivalStats(
  potential: Record<StatId, number>,
  age: number,
  polish: number,
): Record<StatId, number> {
  const out = {} as Record<StatId, number>;
  for (const stat of ['speed', 'stamina', 'accel', 'heart'] as StatId[]) {
    out[stat] = Math.max(1, Math.min(20, Math.round(potential[stat] * ageFactor(age) * polish)));
  }
  return out;
}

export function ratingOf(stats: Record<StatId, number>): number {
  return (stats.speed + stats.stamina + stats.accel + stats.heart) / 4;
}

function makeRival(
  rng: DerbyRngV1,
  id: HorseId,
  league: number,
  season: number,
  age: number,
  sire: Horse | null,
  usedNames?: Set<string>,
): { horse: Horse; info: RivalInfo } {
  const median = LEAGUE_DEFS[league]!.medianLevel;
  const genotype = sire
    ? inheritGenotype(sire.genotype, randomGenotype(rng, median, 3), rng)
    : randomGenotype(rng, median, 3);
  let name = rng.pick(RIVAL_POOL);
  let guard = 0;
  while (usedNames?.has(name) && guard < 40) {
    name = rng.pick(RIVAL_POOL);
    guard += 1;
  }
  usedNames?.add(name);
  const horse = horseFromGenotype(
    id,
    name,
    rng.chance(0.7) ? 'M' : 'F',
    season - age,
    genotype,
    { location: 'external', sireId: sire?.id ?? null },
  );
  const info: RivalInfo = {
    horseId: id,
    league,
    style: rng.pick(STYLES),
    polish: rng.range(0.8, 0.95),
    statFloor: { speed: 1, stamina: 1, accel: 1, heart: 1 },
  };
  horse.stats = rivalStats(horse.potential, age, info.polish);
  return { horse, info };
}

export function makeRivals(
  worldSeed: number,
  startId: HorseId,
): { horses: Horse[]; rivals: RivalInfo[]; nextId: HorseId } {
  const horses: Horse[] = [];
  const rivals: RivalInfo[] = [];
  let id = startId;
  const usedNames = new Set<string>();
  for (let league = 0; league < 3; league += 1) {
    const rng = new DerbyRngV1(hashFnv1a(`${worldSeed}:rivals:${league}`));
    for (let slot = 0; slot < RIVALS_PER_LEAGUE; slot += 1) {
      const { horse, info } = makeRival(rng, id, league, 1, rng.integer(3, 6), null, usedNames);
      horses.push(horse);
      rivals.push(info);
      id += 1;
    }
  }
  return { horses, rivals, nextId: id };
}

/** A replacement youngster for a retired slot — sometimes the old
 * campaigner's own colt, pedigree flavor for free. */
export function makeReplacementRival(
  worldSeed: number,
  season: number,
  league: number,
  id: HorseId,
  retiree: Horse,
): { horse: Horse; info: RivalInfo } {
  const rng = new DerbyRngV1(hashFnv1a(`${worldSeed}:rival-new:${season}:${retiree.id}`));
  const sire = retiree.sex === 'M' && rng.chance(0.3) ? retiree : null;
  const result = makeRival(rng, id, league, season, rng.integer(2, 3), sire);
  if (sire) result.horse.name = `${sire.name.split(' ')[0]}'s Colt`;
  return result;
}
